import { useMemo, useState } from "react";
import { calculate, type CalculatorInput, type CalculationKind, type CalculationResult, type SalaryFrequency, type SeparationReason, type WageZone } from "../engine";
import { ResultPanel } from "../components/ResultPanel";
import { useAuth } from "../auth/auth-context";
import { calculationsApi } from "../api/client";

type Mode = "basic" | "advanced";

interface FormState {
  kind: CalculationKind;
  mode: Mode;
  payDate: string;
  salaryAmount: string;
  frequency: SalaryFrequency;
  zone: WageZone;
  startDate: string;
  endDate: string;
  valuationDate: string;
  reason: SeparationReason;
  unpaidSalaryDays: string;
  unusedVacationDays: string;
  otherOrdinaryPayments: string;
  includeThreeMonths: boolean;
  includeTwentyDays: boolean;
  seniorityMode: "auto" | "yes" | "no";
  aguinaldoDays: string;
  vacationDays: string;
  vacationPremiumPct: string;
  overtimeHours: string;
  sundayShifts: string;
  restDays: string;
  otherTaxable: string;
  otherNonTaxable: string;
  infonavit: string;
  otherDeductions: string;
  riskPremiumPct: string;
  payrollTaxPct: string;
  state: string;
}

const today = new Date().toISOString().slice(0, 10);
const supportedToday =
  today >= "2024-01-01" && today <= "2026-12-31" ? today : "2026-07-28";

const initialState: FormState = {
  kind: "payroll",
  mode: "basic",
  payDate: supportedToday,
  salaryAmount: "",
  frequency: "monthly",
  zone: "general",
  startDate: "2025-01-01",
  endDate: supportedToday,
  valuationDate: supportedToday,
  reason: "resignation",
  unpaidSalaryDays: "0",
  unusedVacationDays: "0",
  otherOrdinaryPayments: "0",
  includeThreeMonths: true,
  includeTwentyDays: false,
  seniorityMode: "auto",
  aguinaldoDays: "15",
  vacationDays: "",
  vacationPremiumPct: "25",
  overtimeHours: "0",
  sundayShifts: "0",
  restDays: "0",
  otherTaxable: "0",
  otherNonTaxable: "0",
  infonavit: "0",
  otherDeductions: "0",
  riskPremiumPct: "0.54355",
  payrollTaxPct: "0",
  state: "",
};

const tabs: Array<{
  id: CalculationKind;
  label: string;
  description: string;
}> = [
  {
    id: "payroll",
    label: "Nómina",
    description: "Bruto, neto, ISR, IMSS y costo patronal.",
  },
  {
    id: "finiquito",
    label: "Finiquito",
    description: "Prestaciones devengadas al terminar.",
  },
  {
    id: "liquidation",
    label: "Liquidación",
    description: "Indemnizaciones en supuestos procedentes.",
  },
  {
    id: "litigation",
    label: "Escenario litigioso",
    description: "Contingencia por despido y duración.",
  },
];

const frequencyLabels: Record<SalaryFrequency, string> = {
  daily: "Diario",
  weekly: "Semanal",
  fourteen: "Catorcenal",
  biweekly: "Quincenal",
  monthly: "Mensual",
};

const reasonOptions: Array<[SeparationReason, string]> = [
  ["resignation", "Renuncia voluntaria"],
  ["mutual", "Mutuo consentimiento"],
  ["justified-dismissal", "Despido justificado"],
  ["unjustified-dismissal", "Despido injustificado"],
  ["employee-rescission", "Rescisión justificada por la persona trabajadora"],
  ["death", "Fallecimiento"],
];

function numberValue(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function Toggle({
  checked,
  onChange,
  title,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  title: string;
  description: string;
}) {
  return (
    <label className="toggle-card">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="toggle-card__control" aria-hidden="true" />
      <span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
    </label>
  );
}

export function CalculatorPage() {
  const duplicate = useMemo(() => {
    const serialized = sessionStorage.getItem("legalflow:duplicate");
    sessionStorage.removeItem("legalflow:duplicate");
    if (!serialized) return null;
    try {
      return JSON.parse(serialized) as CalculatorInput;
    } catch {
      return null;
    }
  }, []);
  const [form, setForm] = useState<FormState>(() => {
    if (!duplicate) return initialState;
    const wage = duplicate.wage;
    return {
      ...initialState,
      kind: duplicate.kind,
      payDate: duplicate.payDate,
      salaryAmount: String(wage.amount),
      frequency: wage.frequency,
      zone: duplicate.zone,
      ...("startDate" in duplicate
        ? {
            startDate: duplicate.startDate,
            endDate: duplicate.endDate,
            reason: duplicate.reason,
          }
        : {}),
      ...("valuationDate" in duplicate
        ? { valuationDate: duplicate.valuationDate }
        : {}),
    };
  });
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [input, setInput] = useState<CalculatorInput | null>(null);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const { user, signIn } = useAuth();

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const buildInput = (): CalculatorInput => {
    const common = {
      payDate: form.payDate,
      wage: {
        amount: numberValue(form.salaryAmount),
        frequency: form.frequency,
      },
      zone: form.zone,
    };
    if (form.kind === "payroll") {
      return {
        kind: "payroll",
        ...common,
        overtimeHours: numberValue(form.overtimeHours),
        sundayShifts: numberValue(form.sundayShifts),
        holidayOrRestDays: numberValue(form.restDays),
        otherTaxableIncome: numberValue(form.otherTaxable),
        otherNonTaxableIncome: numberValue(form.otherNonTaxable),
        infonavitWithholding: numberValue(form.infonavit),
        otherDeductions: numberValue(form.otherDeductions),
        aguinaldoDays: numberValue(form.aguinaldoDays),
        vacationDaysPerYear: numberValue(form.vacationDays) || undefined,
        vacationPremiumRate: numberValue(form.vacationPremiumPct) / 100,
        riskPremiumRate: numberValue(form.riskPremiumPct),
        payrollTaxRate: numberValue(form.payrollTaxPct),
        state: form.state || undefined,
      };
    }

    const separation = {
      ...common,
      startDate: form.startDate,
      endDate: form.endDate,
      reason: form.reason,
      unpaidSalaryDays: numberValue(form.unpaidSalaryDays),
      unusedVacationDays: numberValue(form.unusedVacationDays),
      otherOrdinaryPayments: numberValue(form.otherOrdinaryPayments),
      includeThreeMonths: form.includeThreeMonths,
      includeTwentyDaysPerYear: form.includeTwentyDays,
      includeSeniorityPremium:
        form.seniorityMode === "auto"
          ? undefined
          : form.seniorityMode === "yes",
      aguinaldoDays: numberValue(form.aguinaldoDays),
      vacationDaysPerYear: numberValue(form.vacationDays) || undefined,
      vacationPremiumRate: numberValue(form.vacationPremiumPct) / 100,
    };

    if (form.kind === "litigation") {
      return {
        ...separation,
        kind: "litigation",
        reason:
          form.reason === "employee-rescission"
            ? "employee-rescission"
            : "unjustified-dismissal",
        valuationDate: form.valuationDate,
      };
    }
    return { ...separation, kind: form.kind };
  };

  const handleCalculate = (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSaveMessage("");
    try {
      const nextInput = buildInput();
      const nextResult = calculate(nextInput);
      setInput(nextInput);
      setResult(nextResult);
      window.setTimeout(
        () => document.getElementById("resultado")?.scrollIntoView({ behavior: "smooth" }),
        0,
      );
    } catch (caught) {
      setResult(null);
      setError(
        caught instanceof Error
          ? caught.message
          : "No fue posible realizar el cálculo.",
      );
    }
  };

  const handleSave = async () => {
    if (!user) {
      await signIn();
      return;
    }
    if (!result || !input) return;
    setSaving(true);
    setSaveMessage("");
    try {
      await calculationsApi.create(user.access_token, {
        title: `${result.title} · ${new Intl.DateTimeFormat("es-MX").format(new Date())}`,
        kind: result.kind,
        input,
        result,
      });
      setSaveMessage("Cálculo guardado en tu cuenta.");
    } catch (caught) {
      setSaveMessage(
        caught instanceof Error ? caught.message : "No fue posible guardar.",
      );
    } finally {
      setSaving(false);
    }
  };

  const isSeparation = form.kind !== "payroll";
  const isLiquidation =
    form.kind === "liquidation" || form.kind === "litigation";

  return (
    <>
      <section className="hero">
        <div className="hero__inner">
          <div>
            <span className="eyebrow">México · Matriz normativa 2024–2026</span>
            <h1>Decisiones laborales con números claros.</h1>
            <p>
              Estima nómina, finiquito, liquidación y contingencia litigiosa con
              desglose legal y fiscal. Gratis, sin registro obligatorio.
            </p>
          </div>
          <aside className="trust-card">
            <span className="trust-card__icon" aria-hidden="true">✓</span>
            <div>
              <strong>Privacidad desde el diseño</strong>
              <p>
                Calcula sin cuenta. Sólo guardamos información cuando tú lo
                eliges.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <div className="calculator-layout">
        <section className="calculator-card">
          <header className="calculator-card__header">
            <div>
              <span className="eyebrow">Selecciona un cálculo</span>
              <h2>¿Qué necesitas estimar?</h2>
            </div>
            <div className="mode-switch" aria-label="Nivel de detalle">
              <button
                className={form.mode === "basic" ? "active" : ""}
                onClick={() => set("mode", "basic")}
                type="button"
              >
                Básico
              </button>
              <button
                className={form.mode === "advanced" ? "active" : ""}
                onClick={() => set("mode", "advanced")}
                type="button"
              >
                Avanzado
              </button>
            </div>
          </header>

          <div className="calculation-tabs" role="tablist">
            {tabs.map((tab) => (
              <button
                type="button"
                role="tab"
                aria-selected={form.kind === tab.id}
                className={form.kind === tab.id ? "active" : ""}
                key={tab.id}
                onClick={() => {
                  set("kind", tab.id);
                  if (tab.id === "litigation") {
                    set("reason", "unjustified-dismissal");
                  }
                  setResult(null);
                }}
              >
                <strong>{tab.label}</strong>
                <small>{tab.description}</small>
              </button>
            ))}
          </div>

          <form onSubmit={handleCalculate} className="calculator-form">
            <fieldset>
              <legend>Salario y periodo</legend>
              <div className="form-grid">
                <Field label="Salario bruto">
                  <div className="money-input">
                    <span>$</span>
                    <input
                      required
                      inputMode="decimal"
                      min="0.01"
                      step="0.01"
                      type="number"
                      value={form.salaryAmount}
                      onChange={(event) => set("salaryAmount", event.target.value)}
                      placeholder="30,000.00"
                    />
                  </div>
                </Field>
                <Field label="Periodicidad">
                  <select
                    value={form.frequency}
                    onChange={(event) =>
                      set("frequency", event.target.value as SalaryFrequency)
                    }
                  >
                    {Object.entries(frequencyLabels).map(([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Zona salarial"
                  hint="Sólo afecta mínimos y topes vinculados al salario mínimo."
                >
                  <select
                    value={form.zone}
                    onChange={(event) =>
                      set("zone", event.target.value as WageZone)
                    }
                  >
                    <option value="general">Resto del país</option>
                    <option value="border">Zona Libre de la Frontera Norte</option>
                  </select>
                </Field>
                <Field label="Fecha de pago">
                  <input
                    required
                    type="date"
                    min="2024-01-01"
                    max="2026-12-31"
                    value={form.payDate}
                    onChange={(event) => set("payDate", event.target.value)}
                  />
                </Field>
              </div>
            </fieldset>

            {isSeparation && (
              <fieldset>
                <legend>Relación de trabajo</legend>
                <div className="form-grid">
                  <Field label="Fecha de ingreso">
                    <input
                      required
                      type="date"
                      value={form.startDate}
                      onChange={(event) => set("startDate", event.target.value)}
                    />
                  </Field>
                  <Field label="Fecha de baja o despido">
                    <input
                      required
                      type="date"
                      value={form.endDate}
                      onChange={(event) => set("endDate", event.target.value)}
                    />
                  </Field>
                  {form.kind !== "litigation" && (
                    <Field label="Motivo de terminación">
                      <select
                        value={form.reason}
                        onChange={(event) =>
                          set("reason", event.target.value as SeparationReason)
                        }
                      >
                        {reasonOptions.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </Field>
                  )}
                  {form.kind === "litigation" && (
                    <Field
                      label="Fecha de valuación"
                      hint="Día al que quieres proyectar la contingencia."
                    >
                      <input
                        required
                        type="date"
                        value={form.valuationDate}
                        onChange={(event) =>
                          set("valuationDate", event.target.value)
                        }
                      />
                    </Field>
                  )}
                  <Field label="Días de salario pendientes">
                    <input
                      min="0"
                      step="0.01"
                      type="number"
                      value={form.unpaidSalaryDays}
                      onChange={(event) =>
                        set("unpaidSalaryDays", event.target.value)
                      }
                    />
                  </Field>
                  <Field
                    label="Vacaciones ya devengadas y no gozadas"
                    hint="Días pendientes de periodos completos; la proporción actual es automática."
                  >
                    <input
                      min="0"
                      step="0.01"
                      type="number"
                      value={form.unusedVacationDays}
                      onChange={(event) =>
                        set("unusedVacationDays", event.target.value)
                      }
                    />
                  </Field>
                </div>
              </fieldset>
            )}

            {isLiquidation && (
              <fieldset>
                <legend>Indemnizaciones a modelar</legend>
                <div className="toggle-grid">
                  <Toggle
                    checked={form.includeThreeMonths}
                    onChange={(value) => set("includeThreeMonths", value)}
                    title="Tres meses"
                    description="Indemnización constitucional en los supuestos seleccionados."
                  />
                  <Toggle
                    checked={form.includeTwentyDays}
                    onChange={(value) => set("includeTwentyDays", value)}
                    title="Veinte días por año"
                    description="Actívalo sólo si la acción o convenio lo hace procedente."
                  />
                </div>
              </fieldset>
            )}

            {form.kind === "payroll" && (
              <fieldset>
                <legend>Incidencias del periodo</legend>
                <div className="form-grid">
                  <Field label="Horas extra en la semana">
                    <input
                      min="0"
                      step="0.25"
                      type="number"
                      value={form.overtimeHours}
                      onChange={(event) => set("overtimeHours", event.target.value)}
                    />
                  </Field>
                  <Field label="Domingos laborados">
                    <input
                      min="0"
                      step="1"
                      type="number"
                      value={form.sundayShifts}
                      onChange={(event) => set("sundayShifts", event.target.value)}
                    />
                  </Field>
                  <Field label="Descansos obligatorios laborados">
                    <input
                      min="0"
                      step="1"
                      type="number"
                      value={form.restDays}
                      onChange={(event) => set("restDays", event.target.value)}
                    />
                  </Field>
                </div>
              </fieldset>
            )}

            {form.mode === "advanced" && (
              <fieldset className="advanced-fields">
                <legend>Prestaciones y ajustes avanzados</legend>
                <div className="form-grid">
                  <Field label="Días de aguinaldo al año">
                    <input
                      min="15"
                      step="1"
                      type="number"
                      value={form.aguinaldoDays}
                      onChange={(event) => set("aguinaldoDays", event.target.value)}
                    />
                  </Field>
                  <Field
                    label="Días de vacaciones al año"
                    hint="Déjalo vacío para aplicar la tabla legal por antigüedad."
                  >
                    <input
                      min="12"
                      step="1"
                      type="number"
                      value={form.vacationDays}
                      onChange={(event) => set("vacationDays", event.target.value)}
                    />
                  </Field>
                  <Field label="Prima vacacional">
                    <div className="suffix-input">
                      <input
                        min="25"
                        step="0.01"
                        type="number"
                        value={form.vacationPremiumPct}
                        onChange={(event) =>
                          set("vacationPremiumPct", event.target.value)
                        }
                      />
                      <span>%</span>
                    </div>
                  </Field>
                  {form.kind === "payroll" ? (
                    <>
                      <Field
                        label="Otras percepciones gravadas"
                        hint="Si son fijas e integrables al SBC, valida su integración bimestral por separado."
                      >
                        <input
                          min="0"
                          step="0.01"
                          type="number"
                          value={form.otherTaxable}
                          onChange={(event) =>
                            set("otherTaxable", event.target.value)
                          }
                        />
                      </Field>
                      <Field label="Otras percepciones exentas capturadas">
                        <input
                          min="0"
                          step="0.01"
                          type="number"
                          value={form.otherNonTaxable}
                          onChange={(event) =>
                            set("otherNonTaxable", event.target.value)
                          }
                        />
                      </Field>
                      <Field label="Retención INFONAVIT">
                        <input
                          min="0"
                          step="0.01"
                          type="number"
                          value={form.infonavit}
                          onChange={(event) =>
                            set("infonavit", event.target.value)
                          }
                        />
                      </Field>
                      <Field label="Otras deducciones">
                        <input
                          min="0"
                          step="0.01"
                          type="number"
                          value={form.otherDeductions}
                          onChange={(event) =>
                            set("otherDeductions", event.target.value)
                          }
                        />
                      </Field>
                      <Field label="Prima de riesgo de trabajo">
                        <div className="suffix-input">
                          <input
                            min="0.5"
                            max="15"
                            step="0.00001"
                            type="number"
                            value={form.riskPremiumPct}
                            onChange={(event) =>
                              set("riskPremiumPct", event.target.value)
                            }
                          />
                          <span>%</span>
                        </div>
                      </Field>
                      <Field
                        label="Entidad para impuesto sobre nóminas"
                        hint="El nombre sólo se muestra en el desglose; no se envía al servidor al calcular."
                      >
                        <input
                          maxLength={60}
                          type="text"
                          value={form.state}
                          onChange={(event) => set("state", event.target.value)}
                          placeholder="Ej. Ciudad de México"
                        />
                      </Field>
                      <Field
                        label="Tasa estatal sobre nóminas"
                        hint="Consulta la tasa y exenciones vigentes en la entidad."
                      >
                        <div className="suffix-input">
                          <input
                            min="0"
                            max="10"
                            step="0.01"
                            type="number"
                            value={form.payrollTaxPct}
                            onChange={(event) =>
                              set("payrollTaxPct", event.target.value)
                            }
                          />
                          <span>%</span>
                        </div>
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field label="Otros pagos ordinarios pendientes">
                        <input
                          min="0"
                          step="0.01"
                          type="number"
                          value={form.otherOrdinaryPayments}
                          onChange={(event) =>
                            set("otherOrdinaryPayments", event.target.value)
                          }
                        />
                      </Field>
                      <Field label="Prima de antigüedad">
                        <select
                          value={form.seniorityMode}
                          onChange={(event) =>
                            set(
                              "seniorityMode",
                              event.target.value as FormState["seniorityMode"],
                            )
                          }
                        >
                          <option value="auto">Determinar legalmente</option>
                          <option value="yes">Incluir</option>
                          <option value="no">Excluir</option>
                        </select>
                      </Field>
                    </>
                  )}
                </div>
              </fieldset>
            )}

            {error && <div className="form-error">{error}</div>}
            <div className="form-submit">
              <div>
                <strong>Cálculo local y privado</strong>
                <small>
                  No enviamos los datos al servidor salvo que elijas guardar.
                </small>
              </div>
              <button className="button button--gold button--large" type="submit">
                Calcular ahora
              </button>
            </div>
          </form>
        </section>

        <aside className="calculator-aside">
          <div className="aside-card">
            <span className="eyebrow">Qué obtendrás</span>
            <ul className="check-list">
              <li>Desglose por concepto</li>
              <li>Parte gravada y exenta</li>
              <li>Fundamento legal visible</li>
              <li>Supuestos y alertas</li>
              <li>Versión normativa reproducible</li>
            </ul>
          </div>
          <div className="aside-card aside-card--muted">
            <strong>Precisión condicionada</strong>
            <p>
              Para decisiones reales, usa el modo avanzado y captura prestaciones
              contractuales, pendientes e incidencias completas.
            </p>
          </div>
        </aside>
      </div>

      {result && (
        <div className="result-wrap">
          {saveMessage && <p className="save-message">{saveMessage}</p>}
          <ResultPanel
            result={result}
            saving={saving}
            onSave={() => void handleSave()}
          />
        </div>
      )}
    </>
  );
}
